/**
 * Badge — Componente genérico de badge/etiqueta con variantes de color.
 */

type BadgeVariant = 'emerald' | 'blue' | 'amber' | 'red' | 'slate' | 'purple' | 'cyan' | 'orange' | 'rose' | 'indigo' | 'teal' | 'yellow'

interface BadgeProps {
  label: string
  variant?: BadgeVariant
  dot?: boolean
  className?: string
}

// Las variantes que representan un ESTADO usan los tokens semánticos, así que
// siguen al tema sin repetir el par light+dark. Las demás son cromáticas —el
// llamador elige un color, no un significado— y se quedan con la paleta cruda:
// convertirlas a tokens cambiaría su tono, que es una decisión de diseño.
//
// ⚠️ Las diez variantes de abajo traían clases DUPLICADAS de un barrido previo
// (`dark:bg-x-900/30` dos veces), y `amber` declaraba además `dark:text-amber-300`
// y `dark:text-amber-200` a la vez: con igual especificidad ganaba la emitida
// después —la 300, verificado en el CSS generado— así que la 200 era código
// muerto. Se conservó la que efectivamente se veía.
const variantStyles: Record<BadgeVariant, { bg: string; dot: string }> = {
  emerald: { bg: 'bg-success-surface text-success-content', dot: 'bg-success-solid' },
  amber:   { bg: 'bg-warning-surface text-warning-content', dot: 'bg-warning-solid' },
  red:     { bg: 'bg-danger-surface text-danger-content',   dot: 'bg-danger-solid' },
  blue:    { bg: 'bg-primary-50 text-primary-hover dark:bg-primary-900/30 dark:text-primary-200', dot: 'bg-primary' },
  slate:   { bg: 'bg-surface-muted text-content-muted', dot: 'bg-content-subtle' },
  purple:  { bg: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300', dot: 'bg-purple-500 dark:bg-purple-400' },
  cyan:    { bg: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',         dot: 'bg-cyan-500 dark:bg-cyan-400' },
  orange:  { bg: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300', dot: 'bg-orange-500 dark:bg-orange-400' },
  rose:    { bg: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',         dot: 'bg-rose-500 dark:bg-rose-400' },
  indigo:  { bg: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300', dot: 'bg-indigo-500 dark:bg-indigo-400' },
  teal:    { bg: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',         dot: 'bg-teal-500 dark:bg-teal-400' },
  yellow:  { bg: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300', dot: 'bg-yellow-500 dark:bg-yellow-400' },
}

export function Badge({ label, variant = 'slate', dot = false, className = '' }: BadgeProps) {
  const styles = variantStyles[variant]
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${styles.bg} ${className}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${styles.dot}`} />}
      {label}
    </span>
  )
}

/* ─── StatusBadge — Atajo para active/inactive ──────────────────────────────── */

export function StatusBadge({ active }: { active: boolean }) {
  return active
    ? <Badge label="Activo" variant="emerald" dot />
    : <Badge label="Inactivo" variant="slate" dot />
}

/* ─── Helper para crear badges desde config ─────────────────────────────────── */

export type BadgeConfig<T extends string> = Record<T, { label: string; variant: BadgeVariant }>

export function ConfigBadge<T extends string>({ value, config, dot = true }: {
  value: T; config: BadgeConfig<T>; dot?: boolean
}) {
  const cfg = config[value]
  if (!cfg) return <Badge label={value} variant="slate" dot={dot} />
  return <Badge label={cfg.label} variant={cfg.variant} dot={dot} />
}
